import { useEffect, useMemo, useState } from 'react';
import { PieChart } from 'react-minimal-pie-chart';

import { useChannels } from '@mint-lib/hooks';
import { useTranslation } from '@mint-lib/i18n';
import { snakeCase } from 'snake-case';

import { Box, Card, CardContent, Icon, Typography } from '@myn/mui';

import { channelsArrayType, Version } from '../../../types/versions.js';
import { TOTAL, unLlocatedData } from '../constants/index.jsx';
import ChannelDifferenceTable from './ChannelDifferenceTable.jsx';

type IChannelMixProps = {
  item: Version;
  versions?: Version[];
};

type PieChartDataType = {
  title: string;
  value: number;
  color: string;
};

const ChannelMix: React.FC<IChannelMixProps> = ({ item, versions }) => {
  const [pieChartData, setPieChartData] = useState<PieChartDataType[]>([
    unLlocatedData,
  ]);
  const [channels, setChannels] = useState<channelsArrayType[]>([]);
  const channelColors = useChannels();

  const haveChannels = useMemo(
    () => Object.keys(item.channelMixVersionData?.channelMix || {}).length,
    [item.channelMixVersionData?.channelMix],
  );
  const channelMix = item.channelMixVersionData?.channelMix ?? {};

  const { t } = useTranslation('@myn/planning');

  useEffect(() => {
    const newChannels = Object.entries(
      item.channelMixVersionData?.channelMix || {},
    ).map((ch) => {
      const preparedName = snakeCase(ch?.[0]);

      return {
        label: channelColors[preparedName]?.name,
        bgColor: channelColors[preparedName]?.backgroundColor || 'transparent',
        color: channelColors[preparedName]?.color || 'black',
        icon: (
          <Icon component={channelColors[preparedName]?.icon ?? 'Settings'} />
        ),
        values: ch?.[1],
      };
    });
    setChannels(newChannels);
  }, [item.channelMixVersionData?.channelMix]);

  useEffect(() => {
    const newData = channels.reduce((acc: PieChartDataType[], ch) => {
      const currentPercentNumber = Number(
        ch.values.currentPercent?.replace('%', ''),
      );
      if (currentPercentNumber) {
        return [
          ...acc,
          {
            title: ch.label,
            value: currentPercentNumber,
            color: ch.color,
          },
        ];
      }
      return acc;
    }, []);

    if (newData.length) {
      const unLlocated = newData.reduce((acc, data) => acc - data.value, TOTAL);
      if (unLlocated && unLlocated > 0) {
        setPieChartData([
          ...newData,
          {
            ...unLlocatedData,
            value: unLlocated,
          },
        ]);
        return;
      }
      setPieChartData(newData);
    }
  }, [channels]);

  return (
    <Card>
      <CardContent sx={{ '&, :last-child': { padding: 1.5 } }}>
        <Typography variant="h6">{t('Buying plan channel mix')}</Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '400px auto',
            height: 300,
          }}
        >
          <PieChart
            data={haveChannels ? pieChartData : [unLlocatedData]}
            lineWidth={45}
            radius={150}
            startAngle={270}
            paddingAngle={0.5}
            viewBoxSize={[400, 400]}
            center={[200, 200]}
          />
          {haveChannels ? (
            <Box sx={{ marginTop: 3 }}>
              <ChannelDifferenceTable channels={channelMix} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignSelf: 'center' }}>
              {t('This version has no channels yet.')}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default ChannelMix;
